import { get } from 'lodash';

/**
 * Safely extracts a deal ID from various possible sources, with logging
 * @param {Object} dealInterest - The deal interest object
 * @param {string} [dealName] - Optional deal name to use as fallback
 * @param {boolean} [logDetails=false] - Whether to log debugging details
 * @returns {string|null} - The extracted deal ID or null if not found
 */
export const extractDealId = (dealInterest, dealName = null, logDetails = false) => {
  if (!dealInterest) {
    if (logDetails) console.error('extractDealId: dealInterest is null or undefined');
    return dealName || null;
  }

  // Log the entire object structure if debug is enabled
  if (logDetails) {
    console.log('extractDealId: Called with:', { 
      dealInterestType: typeof dealInterest,
      dealInterestIsArray: Array.isArray(dealInterest),
      dealInterestKeys: typeof dealInterest === 'object' ? Object.keys(dealInterest) : 'N/A',
      dealInterest
    });
  }

  // Different possible ways the deal ID might be stored
  const possiblePaths = [
    'deal.id',                // Direct object reference  
    'dealId',                 // Some components might use this
    'deal_id'                 // Some API responses might use this
  ];

  // Try numeric ID first (preferred)
  for (const path of possiblePaths) {
    const value = get(dealInterest, path);
    if (value && !isNaN(parseInt(value))) {
      if (logDetails) console.log(`extractDealId: Found numeric ID at path ${path}: ${value}`);
      return value.toString(); // Ensure string format to prevent type issues
    }
  }
  
  // Try to lookup inside the dealInterest.deal objects for IDs
  if (dealInterest.deal && typeof dealInterest.deal === 'object') {
    // Check if we can get the numeric ID from the deal object
    if (dealInterest.deal.id && !isNaN(parseInt(dealInterest.deal.id))) {
      if (logDetails) console.log(`extractDealId: Found direct numeric deal.id: ${dealInterest.deal.id}`);
      return dealInterest.deal.id.toString(); // Ensure string format
    }
  }

  // Try deal_name with numeric format check - sometimes deal_name is actually a numeric ID
  const stringPaths = [
    'deal.deal_name',        // Using deal_name as ID
    'deal_name'              // Direct deal_name
  ];
  
  // First try to extract numeric-only deal_name values which are likely IDs
  for (const path of stringPaths) {
    const value = get(dealInterest, path);
    if (value && typeof value === 'string') {
      // If the deal_name is numeric, it's likely an ID
      if (/^\d+$/.test(value)) {
        if (logDetails) console.log(`extractDealId: Found numeric deal_name at path ${path}: ${value}`);
        return value;
      }
    }
  }
  
  // As a fallback, try any deal_name or other string identifiers
  for (const path of stringPaths) {
    const value = get(dealInterest, path);
    if (value && typeof value === 'string') {
      if (logDetails) console.log(`extractDealId: Using deal_name as ID from path ${path}: ${value}`);
      return value;
    }
  }

  // Use provided dealName as fallback
  if (dealName) {
    // Check if dealName is numeric
    if (typeof dealName === 'string' && /^\d+$/.test(dealName)) {
      if (logDetails) console.log(`extractDealId: Using numeric dealName as fallback: ${dealName}`);
      return dealName;
    }
    
    if (logDetails) console.log(`extractDealId: Using provided dealName as fallback: ${dealName}`);
    return dealName;
  }

  if (logDetails) {
    console.error('extractDealId: Failed to find deal ID', { 
      dealInterest, 
      dealInterestId: get(dealInterest, 'id'),
      dealObject: get(dealInterest, 'deal')
    });
  }

  return null;
};

/**
 * Returns HTML element ID safe version of a deal interest ID
 * @param {string} id - The original ID
 * @returns {string} - ID safe for use in HTML element IDs
 */
export const getElementId = (id) => {
  if (!id) return 'unknown';
  return id.toString().replace(/[^a-zA-Z0-9]/g, '_');
};

/**
 * Checks if the deal interest is in the shortlisted state
 * @param {Object} dealInterest - The deal interest to check
 * @returns {boolean} - Whether the deal interest is shortlisted
 */
export const isShortlisted = (dealInterest) => {
  return get(dealInterest, 'workflow_state') === 'shortlisted';
}; 